import {
  Button,
  Checkbox,
  Divider,
  Group,
  Input,
  NumberInput,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";
import { LocalDate, Month, YearMonth } from "@js-joda/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { FormData2, FREQUENCIES, Frequency } from "../GenericTable";
import { DayOfWeek } from "@js-joda/core";
import { recurringScheduleUnitLabels, getMaxInterval } from "./recurrence";
import { ordinal, jsDateToLocalDate } from "./utils";
import { CashflowCapabilities } from "./adapters";
import { getStatutoryWeeklyCapPence } from "../../../../backend/build-ledger";

const daysOfMonthOptions = Array.from({ length: 31 }, (_, i) => {
  const day = i + 1;

  return {
    value: day.toString(),
    label: ordinal(day),
  };
});

type TaxProfile = {
  userId: string;
  userName: string;
};

const humaniseEnum = (s: string) =>
  s.toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

// Live preview of a weeks-based redundancy payment and its £30k tax treatment.
const redundancyPreview = (
  annualSalary: number | null,
  weeks: number | null,
  payBasis: "full" | "statutory",
  endDate: Date | null,
): string => {
  if (!annualSalary || !weeks) {
    return "Tax-free up to £30,000; excess is taxed";
  }
  const fullWeekly = annualSalary / 52;
  const weekly =
    payBasis === "statutory" && endDate
      ? Math.min(
          fullWeekly,
          getStatutoryWeeklyCapPence(jsDateToLocalDate(endDate)) / 100,
        )
      : fullWeekly;
  const payment = weekly * weeks;
  const gbp = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;
  if (payment <= 30_000) {
    return `≈ ${gbp(payment)} — all tax-free`;
  }
  return `≈ ${gbp(payment)} (${gbp(30_000)} tax free, ${gbp(payment - 30_000)} taxable)`;
};

// Live preview of pay in lieu of notice (months of monthly salary, taxable).
const pilonPreview = (
  annualSalary: number | null,
  months: number | null,
): string => {
  if (!annualSalary || !months) {
    return "Paid as this many months of salary, taxable";
  }
  const payment = (annualSalary / 12) * months;
  return `≈ £${Math.round(payment).toLocaleString("en-GB")} (taxable)`;
};

// Live preview of a percentage bonus against the salary.
const bonusPreview = (
  annualSalary: number | null,
  percentage: number | null,
): string => {
  if (!annualSalary || !percentage) return "";
  return `≈ £${Math.round((annualSalary * percentage) / 100).toLocaleString("en-GB")}`;
};

// Live preview of outstanding-holiday pay (annual salary over 260 working days).
const holidayPreview = (
  annualSalary: number | null,
  days: number | null,
): string => {
  if (!annualSalary || !days) {
    return "Paid at your daily rate, fully taxable";
  }
  const payment = (annualSalary / 260) * days;
  return `≈ £${Math.round(payment).toLocaleString("en-GB")} (taxable)`;
};

const getOrdinalOptionsForMonth = (
  month: Month, // 1 indexed
) =>
  Array.from(
    { length: YearMonth.of(2025, month).lengthOfMonth() }, // non-leap year
    (_, i) => ({ label: ordinal(i + 1), value: (i + 1).toString() }),
  );

export const RecurrenceFields = ({
  form,
  taxProfileIds,
  capabilities,
}: {
  form: UseFormReturnType<FormData2>;
  taxProfileIds: TaxProfile[];
  capabilities: CashflowCapabilities;
}) => {
  const {
    scheduleType,
    frequency,
    interval,
    taxProfileId,
    monthlyRule,
    weekday,
  } = form.getValues();

  return (
    <Stack gap="md">
      <Group align="flex-end">
        <TextInput
          required
          label="Description"
          {...form.getInputProps("description")}
        />
        <NumberInput
          required
          label="Amount (£)"
          {...form.getInputProps("amount")}
        />
        {capabilities.taxable && (
          <>
            <Select
              required
              label="Taxable"
              data={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
              value={form.values.isTaxable ? "yes" : "no"}
              onChange={(newValue) => {
                const taxable = newValue === "yes";
                form.setFieldValue("isTaxable", taxable);
                form.setFieldValue(
                  "taxProfileId",
                  taxable ? taxProfileIds[0].userId : null,
                );
              }}
            />
            {form.values.isTaxable && (
              <Select
                data={taxProfileIds.map((x) => ({
                  value: x.userId,
                  label: x.userName,
                }))}
                label="Tax profile"
                required={form.values.isTaxable || false}
                disabled={!form.values.isTaxable}
                value={taxProfileId || ""}
                onChange={(newTaxProfileId) => {
                  form.setFieldValue("taxProfileId", newTaxProfileId);
                }}
              />
            )}
          </>
        )}
      </Group>

      <Divider />

      <Radio.Group
        value={scheduleType}
        onChange={(value) => {
          if (
            value === "one-off" ||
            (value === "repeats" && value !== scheduleType)
          ) {
            if (value === "one-off") {
              form.setFieldValue("frequency", "specific-dates");
              form.setFieldValue("interval", null);
              form.setFieldValue("oneOffDates", [new Date()]);
            } else {
              form.setFieldValue("frequency", "monthly");
              form.setFieldValue("interval", 1);
              form.setFieldValue("monthlyRule", {
                type: "fixed-day",
                day: "1",
              });
            }
            form.setFieldValue("scheduleType", value);
          }
        }}
        label="Occurs"
        withAsterisk
      >
        <Group mt={4} gap="lg">
          <Radio value="repeats" label="Repeats" />
          <Radio value="one-off" label="One-off" />
        </Group>
      </Radio.Group>

      {scheduleType === "repeats" && (
        <Stack gap="xs">
          <Group align="flex-end">
            <NumberInput
              label="Every"
              disabled={frequency === "weekdays"}
              min={1}
              max={getMaxInterval(frequency)}
              step={1}
              allowDecimal={false}
              styles={{ input: { width: 80 } }}
              {...form.getInputProps("interval")}
            />
            <Select
              label="Frequency"
              data={Object.entries(recurringScheduleUnitLabels).map(
                ([value, label]) => ({ value, label }),
              )}
              value={frequency}
              onChange={(newFrequency) => {
                const isFrequency = (value: unknown): value is Frequency =>
                  typeof value === "string" &&
                  (FREQUENCIES as readonly string[]).includes(value);

                if (isFrequency(newFrequency) && newFrequency !== frequency) {
                  form.setFieldValue("frequency", newFrequency);
                  const newMaxInterval = getMaxInterval(newFrequency);

                  if (newFrequency === "monthly") {
                    form.setFieldValue("monthlyType", "same-day-of-month");
                  } else {
                    form.setFieldValue("monthlyType", null);
                  }

                  if (newFrequency === "weekly") {
                    form.setFieldValue("weekday", DayOfWeek.MONDAY.name());
                  }

                  if (newFrequency === "yearly") {
                    form.setFieldValue("yearlyRule", {
                      day: 1,
                      month: Month.MARCH,
                      fromYear: LocalDate.now().year(),
                      toYear: null,
                    });
                  }

                  if (!interval || interval > newMaxInterval) {
                    form.setFieldValue("interval", 1);
                  }
                }
              }}
            />
            <DatePickerInput
              label="From"
              w={160}
              placeholder="Always"
              clearable
              {...form.getInputProps("startsFrom")}
            />
            <DatePickerInput
              label="Until"
              w={160}
              placeholder="Always"
              clearable
              minDate={form.values.startsFrom || undefined}
              {...form.getInputProps("endsOn")}
            />
            {(frequency === "weekly" ||
              frequency === "monthly" ||
              frequency === "yearly") && (
              <Input.Wrapper label="On">
              <Group mt={4} gap="xs">
                {frequency === "weekly" && (
                  <Select
                    w={140}
                    data={[
                      DayOfWeek.MONDAY.name(),
                      DayOfWeek.TUESDAY.name(),
                      DayOfWeek.WEDNESDAY.name(),
                      DayOfWeek.THURSDAY.name(),
                      DayOfWeek.FRIDAY.name(),
                      DayOfWeek.SATURDAY.name(),
                      DayOfWeek.SUNDAY.name(),
                    ]}
                    value={weekday || DayOfWeek.MONDAY.name()}
                    onChange={(newValue) => {
                      form.setFieldValue(
                        "weekday",
                        newValue ?? DayOfWeek.MONDAY.name(),
                      );
                    }}
                  />
                )}

                {frequency === "monthly" && (
                  <>
                    <Select
                      w={180}
                      data={[
                        { value: "fixed-day", label: "Fixed day" },
                        {
                          value: "last-working-day",
                          label: "Last working day",
                        },
                        { value: "last-day", label: "Last day" },
                      ]}
                      value={monthlyRule?.type ?? "fixed-day"}
                      onChange={(newType) => {
                        if (!newType || newType === monthlyRule?.type) return;
                        if (newType === "fixed-day") {
                          form.setFieldValue("monthlyRule", {
                            type: newType,
                            day: "1",
                          });
                        } else {
                          form.setFieldValue("monthlyRule", {
                            type: newType as "last-working-day" | "last-day",
                          });
                        }
                      }}
                    />
                    {monthlyRule?.type === "fixed-day" && (
                      <Select
                        w={100}
                          data={daysOfMonthOptions}
                        value={monthlyRule.day}
                        onChange={(newDay) =>
                          form.setFieldValue("monthlyRule.day", newDay)
                        }
                      />
                    )}
                  </>
                )}

                {frequency === "yearly" && (
                  <>
                    <Select
                      w={130}
                      data={Month.values().map((x) => ({
                        label: humaniseEnum(x.name()),
                        value: x.value().toString(),
                      }))}
                      value={form.values.yearlyRule?.month.value().toString()}
                      onChange={(newValue) => {
                        form.setFieldValue(
                          "yearlyRule.month",
                          newValue ? Month.of(Number(newValue)) : Month.JANUARY,
                        );
                      }}
                    />
                    <Select
                      w={100}
                      data={
                        form.values.yearlyRule
                          ? getOrdinalOptionsForMonth(
                              form.values.yearlyRule.month,
                            )
                          : []
                      }
                      value={form.values.yearlyRule?.day.toString()}
                      onChange={(newValue) => {
                        form.setFieldValue(
                          "yearlyRule.day",
                          newValue ? Number(newValue) : 1,
                        );
                      }}
                    />
                  </>
                )}
              </Group>
              </Input.Wrapper>
            )}
          </Group>
        </Stack>
      )}

      {scheduleType === "one-off" && (
        <Stack gap="xs">
          {form.values.oneOffDates?.map((x, index) => (
            <Group key={index + (x ? x.toISOString() : "")} align="flex-end" gap="xs">
              <DatePickerInput
                label={`Date ${index + 1}`}
                value={x}
                onChange={(newValue) =>
                  form.setFieldValue(`oneOffDates.${index}`, newValue)
                }
              />
              {(form.values.oneOffDates?.length ?? 0) > 1 && (
                <Button
                  size="compact-sm"
                  color="red"
                  variant="subtle"
                  mb={1}
                  onClick={() => form.removeListItem("oneOffDates", index)}
                >
                  <IconTrash size={14} />
                </Button>
              )}
            </Group>
          ))}
          <Button
            size="compact-sm"
            variant="subtle"
            leftSection={<IconPlus size={14} />}
            onClick={() => form.insertListItem("oneOffDates", null)}
          >
            Add date
          </Button>
        </Stack>
      )}

      {capabilities.parentalLeave && (
        <Stack gap="sm">
          <Divider />
          <Checkbox
            label="Parental leave policy"
            checked={!!form.values.parentalLeavePolicy}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                form.setFieldValue("parentalLeavePolicy", {
                  startsFrom: new Date(),
                  afterPolicy: "full-pay",
                  sequentialRules: [
                    {
                      amountType: "smp",
                      percentage: 100,
                      chronoUnits: "MONTHS",
                      amountOfChronoUnits: 12,
                    },
                  ],
                });
              } else {
                form.setFieldValue("parentalLeavePolicy", null);
              }
            }}
          />

          {!!form.values.parentalLeavePolicy && (
            <Stack gap="sm">
              <DatePickerInput
                label="Leave starts from"
                w={200}
                value={form.values.parentalLeavePolicy.startsFrom}
                onChange={(newValue) =>
                  form.setFieldValue("parentalLeavePolicy.startsFrom", newValue)
                }
              />

              <Table withColumnBorders withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={110}>Duration</Table.Th>
                    <Table.Th w={120}>Unit</Table.Th>
                    <Table.Th w={160}>Pay</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {form.values.parentalLeavePolicy.sequentialRules.map(
                    (rule, index) => (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <NumberInput
                            min={1}
                            step={1}
                            allowDecimal={false}
                            styles={{ input: { width: 80 } }}
                            value={rule.amountOfChronoUnits}
                            onChange={(newValue) =>
                              form.setFieldValue(
                                `parentalLeavePolicy.sequentialRules.${index}.amountOfChronoUnits`,
                                Number(newValue),
                              )
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            w={110}
                            data={[
                              { value: "DAYS", label: "Days" },
                              { value: "WEEKS", label: "Weeks" },
                              { value: "MONTHS", label: "Months" },
                              { value: "YEARS", label: "Years" },
                            ]}
                            value={rule.chronoUnits}
                            onChange={(newValue) =>
                              form.setFieldValue(
                                `parentalLeavePolicy.sequentialRules.${index}.chronoUnits`,
                                newValue ?? "MONTHS",
                              )
                            }
                          />
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Select
                              w={130}
                              data={[
                                { value: "percentage", label: "Percentage" },
                                { value: "smp", label: "SMP" },
                              ]}
                              value={rule.amountType}
                              onChange={(newValue) =>
                                form.setFieldValue(
                                  `parentalLeavePolicy.sequentialRules.${index}.amountType`,
                                  newValue ?? "percentage",
                                )
                              }
                            />
                            {rule.amountType === "percentage" && (
                              <NumberInput
                                min={0}
                                max={100}
                                step={1}
                                allowDecimal={false}
                                rightSection={
                                  <Text size="xs" c="dimmed">
                                    %
                                  </Text>
                                }
                                styles={{ input: { width: 70 } }}
                                value={rule.percentage}
                                onChange={(newValue) =>
                                  form.setFieldValue(
                                    `parentalLeavePolicy.sequentialRules.${index}.percentage`,
                                    Number(newValue),
                                  )
                                }
                              />
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td w={40}>
                          <Button
                            size="compact-sm"
                            color="red"
                            variant="subtle"
                            onClick={() =>
                              form.removeListItem(
                                "parentalLeavePolicy.sequentialRules",
                                index,
                              )
                            }
                          >
                            <IconTrash size={14} />
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ),
                  )}
                </Table.Tbody>
              </Table>

              <Button
                size="compact-sm"
                variant="subtle"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  form.insertListItem("parentalLeavePolicy.sequentialRules", {
                    amountType: "percentage",
                    percentage: 100,
                    chronoUnits: "MONTHS",
                    amountOfChronoUnits: 1,
                  })
                }
              >
                Add rule
              </Button>

              <Radio.Group
                label="After leave ends"
                value={form.values.parentalLeavePolicy.afterPolicy}
                onChange={(value) =>
                  form.setFieldValue("parentalLeavePolicy.afterPolicy", value)
                }
              >
                <Group mt="xs" gap="lg">
                  <Radio value="full-pay" label="Back to full pay" />
                  <Radio value="last-rate" label="Stay at last rate" />
                  <Radio value="leave-job" label="Leave job" />
                </Group>
              </Radio.Group>
            </Stack>
          )}
        </Stack>
      )}

      {capabilities.redundancy && scheduleType === "repeats" && (
        <Stack gap="sm">
          <Divider />
          <Checkbox
            label="Redundancy pay"
            checked={!!form.values.redundancy}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                const endDate = form.values.endsOn ?? new Date();
                form.setFieldValue("redundancy", {
                  endDate,
                  paidOn: endDate,
                  weeks: null,
                  payBasis: "full",
                  hasNotice: false,
                  noticeMonths: null,
                  holidayDays: null,
                });
                // Keep the salary's end date in sync with the leaving date.
                form.setFieldValue("endsOn", endDate);
              } else {
                form.setFieldValue("redundancy", null);
              }
            }}
          />

          {!!form.values.redundancy && (
            <Stack gap="sm">
              <Group align="flex-end">
                <DatePickerInput
                  label="Salary stops after"
                  w={180}
                  value={form.values.redundancy.endDate}
                  onChange={(newValue) => {
                    form.setFieldValue("redundancy.endDate", newValue);
                    // Keep the salary's end date in sync with the leaving date.
                    form.setFieldValue("endsOn", newValue);
                  }}
                />
                <DatePickerInput
                  label="Redundancy paid on"
                  description="When the settlement actually hits your account"
                  w={200}
                  value={form.values.redundancy.paidOn}
                  onChange={(newValue) =>
                    form.setFieldValue("redundancy.paidOn", newValue)
                  }
                />
                <NumberInput
                  label="Weeks of redundancy pay"
                  description={redundancyPreview(
                    form.values.amount,
                    form.values.redundancy.weeks,
                    form.values.redundancy.payBasis,
                    form.values.redundancy.endDate,
                  )}
                  min={0}
                  step={0.5}
                  w={260}
                  value={form.values.redundancy.weeks ?? undefined}
                  onChange={(newValue) =>
                    form.setFieldValue(
                      "redundancy.weeks",
                      newValue === "" ? null : Number(newValue),
                    )
                  }
                />
                <Select
                  label="Paid at"
                  w={160}
                  data={[
                    { value: "full", label: "Full pay" },
                    { value: "statutory", label: "Statutory pay" },
                  ]}
                  value={form.values.redundancy.payBasis}
                  onChange={(newValue) =>
                    form.setFieldValue(
                      "redundancy.payBasis",
                      newValue === "statutory" ? "statutory" : "full",
                    )
                  }
                />
              </Group>

              <Checkbox
                label="Payment in lieu of notice (taxable)"
                checked={form.values.redundancy.hasNotice}
                onChange={(e) =>
                  form.setFieldValue(
                    "redundancy.hasNotice",
                    e.currentTarget.checked,
                  )
                }
              />

              {form.values.redundancy.hasNotice && (
                <NumberInput
                  label="Notice (months)"
                  min={0}
                  step={1}
                  allowDecimal={false}
                  w={200}
                  description={pilonPreview(
                    form.values.amount,
                    form.values.redundancy.noticeMonths,
                  )}
                  value={form.values.redundancy.noticeMonths ?? undefined}
                  onChange={(newValue) =>
                    form.setFieldValue(
                      "redundancy.noticeMonths",
                      newValue === "" ? null : Number(newValue),
                    )
                  }
                />
              )}

              <NumberInput
                label="Days of holiday outstanding"
                description={holidayPreview(
                  form.values.amount,
                  form.values.redundancy.holidayDays,
                )}
                min={0}
                step={0.5}
                w={240}
                value={form.values.redundancy.holidayDays ?? undefined}
                onChange={(newValue) =>
                  form.setFieldValue(
                    "redundancy.holidayDays",
                    newValue === "" ? null : Number(newValue),
                  )
                }
              />
            </Stack>
          )}
        </Stack>
      )}

      {capabilities.bonuses && (
        <Stack gap="sm">
          <Divider />
          <Checkbox
            label="Bonuses"
            checked={!!form.values.bonuses}
            onChange={(e) => {
              if (e.currentTarget.checked) {
                form.setFieldValue("bonuses", [
                  {
                    date: new Date(),
                    kind: "percentage",
                    percentage: 10,
                    amount: null,
                  },
                ]);
              } else {
                form.setFieldValue("bonuses", null);
              }
            }}
          />

          {!!form.values.bonuses && (
            <Stack gap="sm">
              <Table withColumnBorders withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={170}>Date due</Table.Th>
                    <Table.Th w={140}>Type</Table.Th>
                    <Table.Th w={220}>Amount</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {form.values.bonuses.map((b, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>
                        <DatePickerInput
                          value={b.date}
                          onChange={(newValue) =>
                            form.setFieldValue(`bonuses.${index}.date`, newValue)
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          w={130}
                          data={[
                            { value: "percentage", label: "Percentage" },
                            { value: "fixed", label: "Fixed (£)" },
                          ]}
                          value={b.kind}
                          onChange={(newValue) =>
                            form.setFieldValue(
                              `bonuses.${index}.kind`,
                              newValue ?? "percentage",
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        {b.kind === "fixed" ? (
                          <NumberInput
                            min={0}
                            w={160}
                            rightSection={
                              <Text size="xs" c="dimmed">
                                £
                              </Text>
                            }
                            value={b.amount ?? undefined}
                            onChange={(newValue) =>
                              form.setFieldValue(
                                `bonuses.${index}.amount`,
                                newValue === "" ? null : Number(newValue),
                              )
                            }
                          />
                        ) : (
                          <Group gap="xs" wrap="nowrap">
                            <NumberInput
                              min={0}
                              max={100}
                              w={90}
                              rightSection={
                                <Text size="xs" c="dimmed">
                                  %
                                </Text>
                              }
                              value={b.percentage ?? undefined}
                              onChange={(newValue) =>
                                form.setFieldValue(
                                  `bonuses.${index}.percentage`,
                                  newValue === "" ? null : Number(newValue),
                                )
                              }
                            />
                            <Text size="xs" c="dimmed">
                              {bonusPreview(form.values.amount, b.percentage)}
                            </Text>
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td w={40}>
                        <Button
                          size="compact-sm"
                          color="red"
                          variant="subtle"
                          onClick={() => form.removeListItem("bonuses", index)}
                        >
                          <IconTrash size={14} />
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              <Button
                size="compact-sm"
                variant="subtle"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  form.insertListItem("bonuses", {
                    date: new Date(),
                    kind: "percentage",
                    percentage: 10,
                    amount: null,
                  })
                }
              >
                Add bonus
              </Button>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
